import { ReactNode, forwardRef, useCallback, useContext, useMemo, useRef, useState } from 'react'

import * as MenuPrimitive from '@radix-ui/react-dropdown-menu'
import { css } from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { Link } from '@redwoodjs/router'

import { TextSize, getFontSize } from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMergeRefs } from 'src/hooks/use-merge-refs'

import Flex from '../Flex/Flex'
import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import { getLinkProps, isInternalLink, LinkActionProps } from '../Link/util'
import Portal from '../Portal/Portal'
import Tooltip from '../Tooltip/Tooltip'

type MenuBaseProps = {
  children: ReactNode
  isModal?: boolean // set to false if opening menu on hover with controlled isOpen state; allows focus to leave the menu
  isOnCloseAutoFocusDisabled?: boolean
  isOpen: boolean
  onOpen?: () => void
  onClose: () => void
  /**
   * Customize which element receives focus when the menu closes; by default it is the element that triggered the menu
   */
  onCloseFocusRef?: React.RefObject<HTMLElement>
}
const MenuBase = ({
  children,
  isModal = true,
  isOnCloseAutoFocusDisabled,
  isOpen,
  onOpen,
  onClose,
  onCloseFocusRef,
}: MenuBaseProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [lastFocusedElement, setLastFocusedElement] = useState<HTMLElement | null>(null)

  const handleToggleIsOpen = useCallback(
    (isEscapeKeyDown?: boolean) => {
      if (isOpen) {
        onClose?.()
        if (isOnCloseAutoFocusDisabled || (!isModal && !isEscapeKeyDown)) {
          return
        }

        requestAnimationFrame(() => {
          const restoreFocusElement =
            onCloseFocusRef?.current ||
            lastFocusedElement ||
            triggerRef.current?.tagName?.toLowerCase() === 'button'
              ? triggerRef.current
              : triggerRef.current?.querySelector('button')
          if (restoreFocusElement) {
            restoreFocusElement.focus()
          }
        })
      } else {
        if (document.activeElement !== document.body) {
          setLastFocusedElement(document.activeElement as HTMLElement | null)
        }
        onOpen?.()
      }
    },
    [
      isOpen,
      onClose,
      isOnCloseAutoFocusDisabled,
      isModal,
      onCloseFocusRef,
      lastFocusedElement,
      onOpen,
    ]
  )

  return (
    <MenuPrimitive.DropdownMenu
      defaultOpen
      open={isOpen}
      onOpenChange={handleToggleIsOpen}
      modal={isModal}
    >
      <MenuContext.Provider value={{ toggleIsOpen: handleToggleIsOpen, triggerRef }}>
        {children}
      </MenuContext.Provider>
    </MenuPrimitive.DropdownMenu>
  )
}

type MenuTriggerProps = Pick<React.HTMLProps<HTMLButtonElement>, 'className' | 'onClick'> & {
  children: ReactNode
  isDisabled?: boolean
  onContextMenu?: (e: React.MouseEvent) => void
}
const MenuTrigger = forwardRef<HTMLButtonElement, MenuTriggerProps>(
  ({ children, className, isDisabled, onClick, onContextMenu }: MenuTriggerProps, ref) => {
    const { triggerRef } = useMenuContext()
    const mergedRef = useMergeRefs(ref, triggerRef)
    return (
      <MenuPrimitive.Trigger
        asChild
        className={className}
        disabled={isDisabled}
        onClick={onClick}
        onContextMenu={onContextMenu}
        ref={mergedRef}
      >
        {children}
      </MenuPrimitive.Trigger>
    )
  }
)

// KEEP IN SYNC WITH MenuContentStyles below
export const menuClassNames = `
  bg-white
  rounded-md
  shadow-md
  p-1
  w-fit
  min-w-[200px]
  max-w-[100vw]
  max-h-[var(--radix-dropdown-menu-content-available-height)]
  overflow-auto
  outline-black
  focus-visible:outline-1
`

/**
 * KEEP IN SYNC WITH menuClassNames above
 * @deprecated Use menuClassNames with Tailwind instead
 */
export const MenuContentStyles = css`
  relative
  background-color: ${({ theme }) => theme.white};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.boxShadowSecondary};
  padding: 4px;
  width: fit-content;
  min-width: 200px;
  max-width: 100vw;
  max-height: var(--radix-dropdown-menu-content-available-height);
  overflow: auto;

  &:focus {
    outline: solid 1px ${({ theme }) => theme.colorPrimaryBorder};
  }
`
type MenuContentProps = Pick<
  MenuPrimitive.DropdownMenuContentProps,
  'align' | 'onMouseLeave' | 'side' | 'sideOffset'
> & {
  children?: ReactNode
  className?: string
}
const MenuContent = forwardRef<HTMLDivElement, MenuContentProps>(
  ({ align = 'start', children, className, onMouseLeave, side, sideOffset = 6 }, ref) => {
    const { toggleIsOpen } = useMenuContext()

    const handleEscapeKeyDown = useCallback(
      (e: KeyboardEvent) => {
        e.preventDefault()
        e.stopPropagation()
        toggleIsOpen(true)
      },
      [toggleIsOpen]
    )

    return (
      <Portal>
        <MenuPrimitive.Content
          onEscapeKeyDown={handleEscapeKeyDown}
          ref={ref}
          align={align}
          className={twMerge(menuClassNames, className)}
          loop
          onMouseLeave={onMouseLeave}
          side={side}
          sideOffset={sideOffset}
        >
          <div className="flex flex-col items-stretch gap-0.5">{children}</div>
        </MenuPrimitive.Content>
      </Portal>
    )
  }
)

type MenuLabelProps = {
  children: ReactNode
}
const MenuLabel = ({ children }: MenuLabelProps) => {
  return <MenuPrimitive.Label>{children}</MenuPrimitive.Label>
}

// KEEP IN SYNC WITH MenuItemStyles below
export const getMenuItemClassNames = (isDisabled?: boolean, isDestructive?: boolean) => `
  cursor-pointer
  [&_*]:cursor-pointer
  rounded-sm
  w-full
  text-sm
  text-left
  [&_svg]:min-w-[16px]
  [&_svg]:w-[16px]

  ${isDisabled ? 'pointer-events-none text-neutral-400 bg-neutral-100' : ''}

  ${isDestructive ? 'hover:text-red-700 [&_.govwell-menu-item-icon]:hover:text-red-700' : ''}

  hover:bg-neutral-100

  focus:menu-item-active
  data-[selected="true"]:menu-item-active
`

export type MenuItemStyleProps = {
  $isDisabled?: boolean
}

/**
 * KEEP IN SYNC WITH getMenuItemClassNames above
 * @deprecated Use getMenuItemClassNames with Tailwind instead
 */
export const MenuItemStyles = css<MenuItemStyleProps>`
  cursor: pointer;
  padding: 6px 9px;
  border-radius: 4px;
  font-size: ${getFontSize(TextSize.Base)}px;
  text-align: left;

  svg {
    min-width: ${getFontSize(TextSize.Base)}px;
    width: ${getFontSize(TextSize.Base)}px;
  }

  ${({ theme, $isDisabled }) =>
    $isDisabled
      ? `
    color: ${theme.colorTextDisabled};
    background-color: ${theme.colorBgContainerDisabled};
    pointer-events: none;
  `
      : `
    &:hover {
      background-color: ${theme.colorBgLayout};
    }

    &:focus,
    &[data-selected='true'] {
      outline: none;
      background-color: ${theme.colorBgLayout};
    }
  `}
`
type MenuItemProps = {
  icon?: IconKey
  isDisabled?: boolean
  isDestructive?: boolean
  children: ReactNode
  className?: string
  /**
   * Optional keyboard shortcut hint rendered as a muted, right-aligned label.
   * Purely informational — the consumer is responsible for actually wiring up
   * the hotkey (e.g., via `react-hotkeys-hook`).
   */
  shortcut?: string
  tooltip?: React.ReactNode
} & LinkActionProps
const MenuItem = (props: MenuItemProps) => {
  const {
    children,
    className,
    externalHref,
    icon,
    isDestructive = false,
    isDisabled = false,
    onClick,
    shortcut,
    tooltip,
  } = props
  const { toggleIsOpen } = useMenuContext()
  const {
    isOpen: isPerformingAction,
    open: startPerformingAction,
    close: stopPerformingAction,
  } = useDisclosure()
  const label = useMemo(() => {
    const leftContent = (
      <Flex align="center" gap={3} className="inline-flex">
        {icon && !isPerformingAction && <Icon icon={icon} className="govwell-menu-item-icon" />}
        {isPerformingAction && <Icon.FaSpinnerThird className="animate-spin text-neutral-400" />}
        {children}
      </Flex>
    )
    if (!shortcut) {
      return leftContent
    }
    // When a shortcut is present we widen to fill the menu item button and
    // push the hint flush right; non-shortcut items keep the original
    // inline-flex layout so existing call sites render identically.
    return (
      <Flex align="center" justify="space-between" gap={4} className="w-full">
        {leftContent}
        <span className="font-mono text-xs text-neutral-400">{shortcut}</span>
      </Flex>
    )
  }, [children, icon, isPerformingAction, shortcut])

  // Single click handler for every variant (button, internal Link, external <a>). Performs the
  // action, awaits any async work so the spinner stays visible, then closes the menu.
  const handleClick = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      if (isPerformingAction) {
        return
      }
      startPerformingAction()
      try {
        await onClick?.(e as React.MouseEvent)
        toggleIsOpen(false)
      } finally {
        stopPerformingAction()
      }
    },
    [isPerformingAction, startPerformingAction, onClick, toggleIsOpen, stopPerformingAction]
  )

  // We render the actual <button> / <a> / <Link> as the root of MenuPrimitive.Item via asChild,
  // so role="menuitem" lands on the same element as the interactive control. That eliminates the
  // `nested-interactive` axe failure where a [role="menuitem"] div wrapped a real <button>/<a>.
  // Because the child element handles its own activation (button onClick or anchor navigation),
  // we always preventDefault on Radix's onSelect — otherwise Radix would auto-close the menu
  // synchronously and short-circuit our async spinner pathway.
  const handleSelect = useCallback((e: Event) => {
    e.preventDefault()
  }, [])

  const contentClassNames = 'px-2 py-1.5 w-full'

  const content = useMemo(() => {
    const { rel, target } = getLinkProps(!!externalHref)
    if (isInternalLink(props)) {
      return (
        <Link
          to={props.internalRoute}
          target={target}
          rel={rel}
          onClick={handleClick}
          className={twMerge(contentClassNames, 'inline-flex items-center gap-1')}
        >
          {label}
        </Link>
      )
    }
    if (externalHref) {
      return (
        <a
          href={externalHref}
          target={target}
          rel={rel}
          onClick={handleClick}
          className={twMerge(contentClassNames, 'inline-flex items-center gap-1')}
        >
          {label} <Icon.FaArrowUpRightFromSquare className="text-sm" />
        </a>
      )
    }
    return (
      <button
        type="button"
        onClick={handleClick}
        className={twMerge(contentClassNames, isPerformingAction ? 'text-neutral-400' : '')}
        disabled={isPerformingAction}
      >
        {label}
      </button>
    )
  }, [externalHref, handleClick, isPerformingAction, label, props])

  // The asChild form forwards Radix's role="menuitem", tabindex, and data-* attributes onto
  // `content`'s root element (a real button or anchor), which is the structural fix for both
  // `aria-allowed-attr` and `nested-interactive`.
  const itemNode = (
    <MenuPrimitive.Item
      asChild
      className={twMerge(getMenuItemClassNames(isDisabled, isDestructive), className)}
      disabled={isDisabled}
      onSelect={handleSelect}
    >
      {content}
    </MenuPrimitive.Item>
  )

  // Tooltips on disabled menu items still need a non-pointer-events-none surrogate — the tooltip
  // trigger can't fire on a disabled element directly. We keep the wrapping <div> only in the
  // tooltip case so non-tooltip items don't add an extra DOM layer for no reason.
  if (tooltip) {
    return (
      <Tooltip content={tooltip}>
        <div>{itemNode}</div>
      </Tooltip>
    )
  }

  return itemNode
}

type MenuContextType = {
  toggleIsOpen: (isEscapeKeyDown?: boolean) => void
  triggerRef: React.RefObject<HTMLButtonElement>
}

const MenuContext = React.createContext<MenuContextType>({} as MenuContextType)
export const useMenuContext = () => useContext(MenuContext)

const Menu = Object.assign(MenuBase, {
  Content: MenuContent,
  Trigger: MenuTrigger,
  Label: MenuLabel,
  Item: MenuItem,
  Separator: MenuPrimitive.Separator,
})

export default Menu
